import React, { useEffect, useState } from 'react';
import { Alert, Card } from 'react-bootstrap';
import userService from '../../services/user.service';
import Loading from '../common/Loading';
import { useEyeconxStore } from '../../store/eyeconxStore';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from '../helpers/swal';

const ROLE_BADGE = {
    ADMIN: 'profile-role-badge profile-role-badge--admin',
};

const InfoRow = ({ label, children }) => (
    <div className="notifications-info-row">
        <span className="notifications-info-label">{label}</span>
        <span className="notifications-info-value">{children ?? '—'}</span>
    </div>
);

const Profile = () => {
    const user = useEyeconxStore((state) => state.user);
    const clinicId = useEyeconxStore((state) => state.clinicId);
    const logout = useEyeconxStore((state) => state.logout);
    const navigate = useNavigate();
    const { userId: routeUserId } = useParams();

    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);

    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordData, setPasswordData] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [changingPassword, setChangingPassword] = useState(false);
    const [passwordErrors, setPasswordErrors] = useState({});

    const resolvedUserId = routeUserId || user?.ID;

    useEffect(() => {
        fetchUserProfile();
    }, [clinicId, resolvedUserId]);

    const fetchUserProfile = async () => {
        try {
            setLoading(true);
            setError('');
            if (clinicId && resolvedUserId) {
                const response = await userService.getUser(clinicId, resolvedUserId);
                setProfile(response.data);
                setFormData(response.data);
            }
        } catch (err) {
            setError('Failed to fetch user profile');
            console.error('Error fetching profile:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleEditToggle = () => {
        if (isEditing) {
            setFormData(profile);
            setError('');
        }
        setIsEditing(!isEditing);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            setError('');

            if (!clinicId || !resolvedUserId) {
                setError('Missing clinic ID or user ID');
                return;
            }

            const updateData = {
                firstName: formData.firstName,
                lastName: formData.lastName,
                username: formData.username,
                email: formData.email,
                isActive: formData.isActive,
                isAuthenticated: formData.isAuthenticated,
                role: formData.role,
            };

            const response = await userService.updateUser(resolvedUserId, clinicId, updateData);

            setProfile(response.data);
            setFormData(response.data);
            setIsEditing(false);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Profile updated successfully!',
                timer: 3000,
                showConfirmButton: false,
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to update profile';
            setError(errorMessage);

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                timer: 4000,
                showConfirmButton: true,
            });
        } finally {
            setSaving(false);
        }
    };

    const handleChangePasswordToggle = () => {
        setShowChangePassword(!showChangePassword);
        setPasswordData({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
        });
        setPasswordErrors({});
        setError('');
    };

    const handlePasswordInputChange = (e) => {
        const { name, value } = e.target;
        setPasswordData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (passwordErrors[name]) {
            setPasswordErrors((prev) => ({
                ...prev,
                [name]: '',
            }));
        }
    };

    const validatePasswordForm = () => {
        const errors = {};

        if (!passwordData.oldPassword) {
            errors.oldPassword = 'Old password is required';
        }

        if (!passwordData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (passwordData.newPassword.length < 10) {
            errors.newPassword = 'Password must be at least 10 characters long';
        }

        if (!passwordData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your new password';
        } else if (passwordData.newPassword !== passwordData.confirmPassword) {
            errors.confirmPassword = 'New password and confirm password do not match';
        }

        setPasswordErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const handleChangePassword = async () => {
        if (!validatePasswordForm()) {
            return;
        }

        try {
            setChangingPassword(true);
            setError('');

            if (!clinicId || !resolvedUserId) {
                setError('Missing clinic ID or user ID');
                return;
            }

            await userService.changePassword(resolvedUserId, clinicId, {
                oldPassword: passwordData.oldPassword,
                newPassword: passwordData.newPassword,
            });

            setShowChangePassword(false);
            setPasswordData({
                oldPassword: '',
                newPassword: '',
                confirmPassword: '',
            });

            Swal.fire({
                icon: 'success',
                title: 'Password Changed!',
                html: 'Password changed successfully!<br>You will be logged out in <strong id="countdown">5</strong> seconds for security reasons.',
                timer: 5000,
                timerProgressBar: true,
                showConfirmButton: false,
                didOpen: () => {
                    const countdownEl = document.getElementById('countdown');
                    if (countdownEl) {
                        const interval = setInterval(() => {
                            const timeLeft = Swal.getTimerLeft();
                            if (countdownEl) {
                                countdownEl.textContent = Math.ceil(timeLeft / 1000);
                            }
                            if (timeLeft <= 0) {
                                clearInterval(interval);
                            }
                        }, 100);
                    }
                },
            }).then(() => {
                handleLogout();
            });
        } catch (err) {
            const errorMessage = err.response?.data?.message || 'Failed to change password';
            setError(errorMessage);

            Swal.fire({
                icon: 'error',
                title: 'Error!',
                text: errorMessage,
                timer: 4000,
                showConfirmButton: true,
            });
        } finally {
            setChangingPassword(false);
        }
    };

    const initials = `${profile?.firstName?.charAt(0) || ''}${profile?.lastName?.charAt(0) || ''}`;

    return (
        <div className="container-fluid">
            <div className="pagetitle d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h1 className="mb-0">
                    <i className="bi bi-person"></i> User Profile
                </h1>
            </div>

            <Card className="profile-card shadow-sm border-0">
                <Card.Body className="p-3">
                    {loading ? (
                        <Loading />
                    ) : error && !profile ? (
                        <Alert variant="warning" className="border-0 mb-0">
                            {error}
                        </Alert>
                    ) : !profile ? (
                        <Alert variant="warning" className="border-0 mb-0">
                            No profile data found
                        </Alert>
                    ) : (
                        <div className="profile-page">
                            {error && (
                                <Alert
                                    variant="danger"
                                    className="border-0"
                                    dismissible
                                    onClose={() => setError('')}
                                >
                                    {error}
                                </Alert>
                            )}

                            <div className="profile-page__header">
                                <div className="d-flex align-items-center gap-3 flex-wrap">
                                    <div className="profile-avatar" aria-hidden="true">
                                        {initials || <i className="bi bi-person"></i>}
                                    </div>
                                    <div>
                                        <h5 className="profile-page__title mb-1">
                                            {profile.firstName} {profile.lastName}
                                        </h5>
                                        <div className="d-flex align-items-center flex-wrap gap-2 mb-1">
                                            <span
                                                className={
                                                    ROLE_BADGE[profile.role] ||
                                                    'profile-role-badge profile-role-badge--other'
                                                }
                                            >
                                                {profile.role || '—'}
                                            </span>
                                            <span
                                                className={`profile-status-badge ${
                                                    profile.isActive
                                                        ? 'profile-status-badge--on'
                                                        : 'profile-status-badge--off'
                                                }`}
                                            >
                                                <span className="profile-status-dot"></span>
                                                {profile.isActive ? 'Active' : 'Inactive'}
                                            </span>
                                            <span
                                                className={`profile-status-badge ${
                                                    profile.isAuthenticated
                                                        ? 'profile-status-badge--on'
                                                        : 'profile-status-badge--off'
                                                }`}
                                            >
                                                <span className="profile-status-dot"></span>
                                                {profile.isAuthenticated
                                                    ? 'Authenticated'
                                                    : 'Not authenticated'}
                                            </span>
                                        </div>
                                        <p className="text-muted small mb-0">{profile.email}</p>
                                    </div>
                                </div>
                                <div className="d-flex flex-wrap gap-2">
                                    {isEditing ? (
                                        <button
                                            type="button"
                                            className="btn btn-outline-danger btn-sm"
                                            onClick={handleEditToggle}
                                            disabled={saving || changingPassword}
                                        >
                                            <i className="bi bi-x me-1"></i>
                                            Cancel
                                        </button>
                                    ) : (
                                        <button
                                            type="button"
                                            className="btn btn-outline-primary btn-sm"
                                            onClick={handleEditToggle}
                                            disabled={saving || changingPassword}
                                        >
                                            <i className="bi bi-pencil me-1"></i>
                                            Edit Profile
                                        </button>
                                    )}
                                </div>
                            </div>

                            <div className="row g-3">
                                <div className="col-lg-8">
                                    <div className="notifications-panel">
                                        <div className="notifications-panel__header">
                                            <i className="bi bi-person-lines-fill"></i>
                                            Profile Details
                                        </div>
                                        <div className="notifications-panel__body">
                                            <div className="row g-3">
                                                <div className="col-md-6">
                                                    <label
                                                        htmlFor="profile-firstName"
                                                        className="notifications-form-label"
                                                    >
                                                        First Name
                                                    </label>
                                                    <input
                                                        id="profile-firstName"
                                                        type="text"
                                                        name="firstName"
                                                        className="form-control form-control-sm"
                                                        value={formData.firstName || ''}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing || saving}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label
                                                        htmlFor="profile-lastName"
                                                        className="notifications-form-label"
                                                    >
                                                        Last Name
                                                    </label>
                                                    <input
                                                        id="profile-lastName"
                                                        type="text"
                                                        name="lastName"
                                                        className="form-control form-control-sm"
                                                        value={formData.lastName || ''}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing || saving}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label
                                                        htmlFor="profile-username"
                                                        className="notifications-form-label"
                                                    >
                                                        Username
                                                    </label>
                                                    <input
                                                        id="profile-username"
                                                        type="text"
                                                        name="username"
                                                        className="form-control form-control-sm"
                                                        value={formData.username || ''}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing || saving}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label
                                                        htmlFor="profile-email"
                                                        className="notifications-form-label"
                                                    >
                                                        Email
                                                    </label>
                                                    <input
                                                        id="profile-email"
                                                        type="email"
                                                        name="email"
                                                        className="form-control form-control-sm"
                                                        value={formData.email || ''}
                                                        onChange={handleInputChange}
                                                        disabled={!isEditing || saving}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label className="notifications-form-label">
                                                        Account Status
                                                    </label>
                                                    <div className="d-flex flex-column gap-2 pt-1">
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="isActive"
                                                                checked={formData.isActive || false}
                                                                onChange={handleInputChange}
                                                                disabled={!isEditing || saving}
                                                                id="profile-isActive"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="profile-isActive"
                                                            >
                                                                Active
                                                            </label>
                                                        </div>
                                                        <div className="form-check">
                                                            <input
                                                                className="form-check-input"
                                                                type="checkbox"
                                                                name="isAuthenticated"
                                                                checked={formData.isAuthenticated || false}
                                                                onChange={handleInputChange}
                                                                disabled={!isEditing || saving}
                                                                id="profile-isAuthenticated"
                                                            />
                                                            <label
                                                                className="form-check-label"
                                                                htmlFor="profile-isAuthenticated"
                                                            >
                                                                Authenticated
                                                            </label>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {isEditing && (
                                                <div className="profile-form-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={handleEditToggle}
                                                        disabled={saving}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm"
                                                        onClick={handleSave}
                                                        disabled={saving}
                                                    >
                                                        {saving ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-1"
                                                                    role="status"
                                                                ></span>
                                                                Saving…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-floppy me-1"></i>
                                                                Save Changes
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {showChangePassword && (
                                        <div className="notifications-panel mt-3">
                                            <div className="notifications-panel__header justify-content-between flex-wrap gap-2">
                                                <span className="d-flex align-items-center gap-2">
                                                    <i className="bi bi-key"></i>
                                                    Change Password
                                                </span>
                                                <button
                                                    type="button"
                                                    className="btn btn-outline-danger btn-sm"
                                                    onClick={handleChangePasswordToggle}
                                                    disabled={changingPassword}
                                                >
                                                    <i className="bi bi-x me-1"></i>
                                                    Cancel
                                                </button>
                                            </div>
                                            <div className="notifications-panel__body">
                                                <div className="row g-3">
                                                    <div className="col-md-4">
                                                        <label
                                                            htmlFor="profile-oldPassword"
                                                            className="notifications-form-label"
                                                        >
                                                            Old Password
                                                        </label>
                                                        <input
                                                            id="profile-oldPassword"
                                                            type="password"
                                                            name="oldPassword"
                                                            className={`form-control form-control-sm ${
                                                                passwordErrors.oldPassword ? 'is-invalid' : ''
                                                            }`}
                                                            value={passwordData.oldPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="Enter old password"
                                                        />
                                                        {passwordErrors.oldPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.oldPassword}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label
                                                            htmlFor="profile-newPassword"
                                                            className="notifications-form-label"
                                                        >
                                                            New Password
                                                        </label>
                                                        <input
                                                            id="profile-newPassword"
                                                            type="password"
                                                            name="newPassword"
                                                            className={`form-control form-control-sm ${
                                                                passwordErrors.newPassword ? 'is-invalid' : ''
                                                            }`}
                                                            value={passwordData.newPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="At least 10 characters"
                                                        />
                                                        {passwordErrors.newPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.newPassword}
                                                            </div>
                                                        )}
                                                        <div className="notifications-form-hint">
                                                            Password must be at least 10 characters long
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <label
                                                            htmlFor="profile-confirmPassword"
                                                            className="notifications-form-label"
                                                        >
                                                            Confirm Password
                                                        </label>
                                                        <input
                                                            id="profile-confirmPassword"
                                                            type="password"
                                                            name="confirmPassword"
                                                            className={`form-control form-control-sm ${
                                                                passwordErrors.confirmPassword
                                                                    ? 'is-invalid'
                                                                    : ''
                                                            }`}
                                                            value={passwordData.confirmPassword}
                                                            onChange={handlePasswordInputChange}
                                                            disabled={changingPassword}
                                                            placeholder="Confirm new password"
                                                        />
                                                        {passwordErrors.confirmPassword && (
                                                            <div className="invalid-feedback">
                                                                {passwordErrors.confirmPassword}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="profile-form-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-secondary btn-sm"
                                                        onClick={handleChangePasswordToggle}
                                                        disabled={changingPassword}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-success btn-sm"
                                                        onClick={handleChangePassword}
                                                        disabled={changingPassword}
                                                    >
                                                        {changingPassword ? (
                                                            <>
                                                                <span
                                                                    className="spinner-border spinner-border-sm me-1"
                                                                    role="status"
                                                                ></span>
                                                                Changing…
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="bi bi-check-lg me-1"></i>
                                                                Change Password
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                <div className="col-lg-4 d-flex flex-column gap-3">
                                    <div className="notifications-panel">
                                        <div className="notifications-panel__header">
                                            <i className="bi bi-gear"></i>
                                            Account Actions
                                        </div>
                                        <div className="notifications-panel__body d-grid gap-2">
                                            <button
                                                type="button"
                                                className={`btn btn-sm ${
                                                    showChangePassword
                                                        ? 'btn-outline-secondary'
                                                        : 'btn-outline-primary'
                                                }`}
                                                onClick={handleChangePasswordToggle}
                                                disabled={saving || changingPassword}
                                            >
                                                <i className="bi bi-key me-1"></i>
                                                {showChangePassword
                                                    ? 'Hide Password Form'
                                                    : 'Change Password'}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="notifications-panel">
                                        <div className="notifications-panel__header">
                                            <i className="bi bi-info-circle"></i>
                                            Account Information
                                        </div>
                                        <div className="notifications-panel__body">
                                            <InfoRow label="User ID">
                                                <span className="font-monospace small">{profile.id}</span>
                                            </InfoRow>
                                            <InfoRow label="User Number">{profile.number || '—'}</InfoRow>
                                            <InfoRow label="Created At">{profile.createdAt || '—'}</InfoRow>
                                            <InfoRow label="Last Modified">
                                                {profile.modifiedAt || '—'}
                                            </InfoRow>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
};

export default Profile;
